/**
 * QuickConnect (QuickCollect) greeting template rendering.
 *
 * Greetings are authored as HTML in the admin RichTextEditor and stored on
 * `orgQCTemplates.greeting`. They support two tokens:
 *
 *   {Org Name}   -> the organization name
 *   {Area Name}  -> the area (exhibition) name
 *
 * Token names match the spaced Title Case convention used by the narrator email
 * templates. `{OrgName}` / `{AreaName}` are also accepted as legacy aliases so
 * existing seed records keep working.
 *
 * See docs/QUICKCOLLECT_IMPLEMENTATION.md
 */

export const QC_GREETING_TOKENS = ["{Org Name}", "{Area Name}"];

const escapeHtml = (value) => {
  if (value === null || value === undefined) return "";
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
};

/**
 * Substitute tokens into a greeting template.
 *
 * Token values are HTML-escaped to prevent injection. The caller is still
 * responsible for sanitizing the resulting HTML (DOMPurify) before rendering,
 * since the template body itself is admin-authored HTML.
 *
 * @param {object} params
 * @param {string} params.greeting - raw template HTML
 * @param {string} params.orgName
 * @param {string} params.areaName
 * @returns {string} greeting HTML with tokens replaced
 */
export const buildQCGreetingHtml = ({ greeting, orgName, areaName }) => {
  if (!greeting || typeof greeting !== "string") return "";

  const org = escapeHtml(orgName || "");
  const area = escapeHtml(areaName || "");

  return greeting
    .replaceAll("{Org Name}", org)
    .replaceAll("{OrgName}", org)
    .replaceAll("{Area Name}", area)
    .replaceAll("{AreaName}", area)
    .trim();
};

/** Strip HTML tags for a plain-text table preview. */
export const stripHtml = (html) => {
  if (!html || typeof html !== "string") return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
};